import { useCallback } from 'react';

const BASE_PER_BAG_REWARD_AMOUNT = 2;

export const getMilestoneDisplayAmount = (
  bonusAmount,
  bagNumber,
  baseAmount = BASE_PER_BAG_REWARD_AMOUNT,
) => {
  const parsedBonusAmount = Number(bonusAmount);
  if (!Number.isFinite(parsedBonusAmount)) {
    return baseAmount;
  }

  if (Number(bagNumber) === 1) {
    return parsedBonusAmount;
  }

  return parsedBonusAmount + baseAmount;
};

const MilestoneProgressBar = ({
  milestoneThresholds,
  markerBagNumbers,
  allMilestonesCompleted,
  previousMilestoneBagNumber,
  previousMilestoneBonusAmount,
  showPerBagRewardAmount,
  currentBagNumberInCycle,
  currentMarkerBagNumber,
  activeMilestoneBonusAmount,
  progressTrackWidthStyle,
  currentRangeIndex,
}) => {
  const safeMarkerBagNumbers = Array.isArray(markerBagNumbers) ? markerBagNumbers : [];
  const safeMilestoneThresholds = Array.isArray(milestoneThresholds) ? milestoneThresholds : [];
  const milestoneTrackHorizontalPaddingPx = 32;
  const milestoneDotSizePx = 10;
  const milestoneNodeCenterInsetPx = milestoneTrackHorizontalPaddingPx + (milestoneDotSizePx / 2);
  const milestoneNextPillLeftOffsetPx = 0;
  const showNextPill = safeMilestoneThresholds.length > 0 && safeMarkerBagNumbers.length > 0;
  const safeCurrentRangeIndex = safeMarkerBagNumbers.length > 0
    ? Math.min(Math.max(Number(currentRangeIndex) || 0, 0), safeMarkerBagNumbers.length - 1)
    : 0;

  const getSegmentBagNodeLeft = useCallback((index) => {
    if (safeMarkerBagNumbers.length <= 1) {
      return '50%';
    }

    const safeIndex = Math.min(Math.max(Number(index) || 0, 0), safeMarkerBagNumbers.length - 1);
    return `calc(${milestoneNodeCenterInsetPx}px + ((100% - ${(milestoneNodeCenterInsetPx * 2)}px) * ${safeIndex} / ${safeMarkerBagNumbers.length - 1}) - ${milestoneNextPillLeftOffsetPx}px)`;
  }, [
    safeMarkerBagNumbers.length,
    milestoneNodeCenterInsetPx,
    milestoneNextPillLeftOffsetPx,
  ]);
  const nextPillLeft = getSegmentBagNodeLeft(safeCurrentRangeIndex);

  return (
    <>
      {!!safeMilestoneThresholds.length && (
        <div className="MyAccount__milestones-amounts">
          {safeMarkerBagNumbers.map((bagNumber, index) => {
            const isMilestoneBag = index === safeMarkerBagNumbers.length - 1;
            const isLeadingMilestoneBag = bagNumber === previousMilestoneBagNumber
              && previousMilestoneBagNumber > 0;
            const isCompleted = allMilestonesCompleted
              ? true
              : bagNumber <= currentBagNumberInCycle;
            const isCurrent = !allMilestonesCompleted && bagNumber === currentMarkerBagNumber;
            let displayAmount = BASE_PER_BAG_REWARD_AMOUNT;
            if (isMilestoneBag) {
              displayAmount = getMilestoneDisplayAmount(activeMilestoneBonusAmount, bagNumber);
            } else if (isLeadingMilestoneBag) {
              displayAmount = getMilestoneDisplayAmount(
                previousMilestoneBonusAmount,
                previousMilestoneBagNumber,
              );
            } else if (!showPerBagRewardAmount) {
              displayAmount = null;
            }
            const showPlusPrefix = showPerBagRewardAmount && isCompleted && displayAmount !== null;

            return (
              <span
                key={`milestone-amount-${bagNumber}`}
                style={{ left: getSegmentBagNodeLeft(index) }}
                className={`
                  ${displayAmount === null ? 'is-pill-placeholder' : ''}
                  ${isCompleted ? 'is-completed' : ''}
                  ${isCurrent ? 'is-current' : ''}
                `}
              >
                <strong>{displayAmount === null ? '$0' : `${showPlusPrefix ? '+ ' : ''}$${displayAmount}`}</strong>
              </span>
            );
          })}
        </div>
      )}
      <div className="MyAccount__milestones-track-wrap">
        <div className="MyAccount__milestones-track">
          <span
            className="active"
            style={{ width: progressTrackWidthStyle }}
          />
          {safeMarkerBagNumbers.map((bagNumber, index) => {
            const key = bagNumber;
            const isMilestoneBag = index === safeMarkerBagNumbers.length - 1;
            const isCompleted = allMilestonesCompleted
              ? true
              : bagNumber <= currentBagNumberInCycle;
            const isCurrentBag = !allMilestonesCompleted && bagNumber === currentMarkerBagNumber;
            let markerNode = <i />;
            if (isMilestoneBag && isCompleted) {
              markerNode = <i className="is-check" />;
            }

            return (
              <span
                key={`milestone-dot-${key}`}
                style={{ left: getSegmentBagNodeLeft(index) }}
                className={`
                  ${isMilestoneBag ? 'is-milestone-marker' : ''}
                  ${isCompleted ? 'is-completed' : ''}
                  ${isCurrentBag ? 'is-current-glow' : ''}
                `}
              >
                {markerNode}
              </span>
            );
          })}
        </div>
        {showNextPill && (
          <div
            className="MyAccount__milestones-next-pill"
            style={{ left: nextPillLeft }}
            aria-hidden="true"
          />
        )}
      </div>
    </>
  );
};

export default MilestoneProgressBar;
